import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ThemePalette } from '@angular/material/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FieldTypeConfig } from '@ngx-formly/core';
import { FieldType, FormlyFieldProps } from '@ngx-formly/material/form-field';
import { addDays, startOfMonth, startOfWeek, startOfYear } from 'date-fns';
import { tap } from 'rxjs';

export enum FormlyDateRangeOptions {
  ALL = 'Show All',
  THIS_WEEK = 'This week',
  THIS_MONTH = 'This month',
  LAST_SIXTY_DAYS = 'Last 60 days',
  THIS_YEAR = 'This year',
  CUSTOM = 'Custom date range',
}

export interface FormlyDateRange {
  start?: Date;
  end?: Date;
}

export interface FormlyDateRangePickerFieldProps extends FormlyFieldProps {
  color?: ThemePalette;
}

@UntilDestroy()
@Component({
  selector: 'vmspdb-component-formly-daterangepicker',
  templateUrl: './formly-daterangepicker.component.html',
  styleUrls: ['./formly-daterangepicker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FormlyDateRangePickerComponent
  extends FieldType<FieldTypeConfig<FormlyDateRangePickerFieldProps>>
  implements AfterViewInit
{
  readonly FormlyDateRangeOptions = FormlyDateRangeOptions;
  selectedDateOption = FormlyDateRangeOptions.LAST_SIXTY_DAYS;
  dateOptions = Object.values(FormlyDateRangeOptions);

  formGroup = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  dateRangeSetValue(start: Date | null, end: Date | null) {
    if (start && !end) {
      return;
    }

    const value: FormlyDateRange =
      start && end
        ? {
            start,
            end,
          }
        : {};

    this.formControl.setValue(value);
    this.formControl.markAsDirty();
  }

  ngAfterViewInit() {
    const props: any = this.props;
    if (props['defaultSelectedDate']) {
      this.selectedDateOption = props['defaultSelectedDate'];
    }

    this.formGroup.valueChanges
      .pipe(
        untilDestroyed(this),
        tap(({ start, end }) => {
          this.dateRangeSetValue(start ?? null, end ?? null);
        }),
      )
      .subscribe();
    this.updateDateRange();
  }

  updateDateRange() {
    const end = new Date();

    switch (this.selectedDateOption) {
      case FormlyDateRangeOptions.LAST_SIXTY_DAYS:
        this.formGroup.patchValue({
          start: addDays(end, -60),
          end,
        });
        break;
      case FormlyDateRangeOptions.THIS_MONTH:
        this.formGroup.patchValue({
          start: startOfMonth(end),
          end,
        });
        break;
      case FormlyDateRangeOptions.THIS_WEEK:
        this.formGroup.patchValue({
          start: startOfWeek(end),
          end,
        });
        break;
      case FormlyDateRangeOptions.THIS_YEAR:
        this.formGroup.patchValue({
          start: startOfYear(end),
          end,
        });
        break;
      case FormlyDateRangeOptions.ALL:
        this.formGroup.patchValue({
          start: null,
          end: null,
        });
        break;
    }
  }
}
